import React, {useState, useEffect} from 'react';
import styled from 'styled-components';
import {Paginator, PaginatorPageChangeEvent} from 'primereact/paginator';

interface Column {
    title: React.ReactNode;
    render: (data: any) => React.ReactNode;
    width?: string;
    flex?: string;
}

interface TableProps {
    columns: Column[];
    data: any;
    totalRecords?: number;
    loading?: boolean;
    onPageChange?: (page: number, rows: number) => void;
    initialPage?: number;
    initialRows?: number;
}

export const Table: React.FC<TableProps> = ({
                                                columns,
                                                data,
                                                totalRecords = data.length,
                                                loading = false,
                                                onPageChange,
                                                initialPage = 1,
                                                initialRows = 10
                                            }) => {
    const [first, setFirst] = useState(0);
    const [pageCount, setPageCount] = useState(initialRows);
    const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches);
    const [displayData, setDisplayData] = useState<any[]>([]);

    useEffect(() => {
        if (!onPageChange) {
            setDisplayData(data.slice(first, first + pageCount));
        } else {
            setDisplayData(data);
        }
    }, [data, first, pageCount, onPageChange]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const mql = window.matchMedia("(max-width: 768px)");
        const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
        setIsMobile(mql.matches);
        mql.addEventListener?.("change", onChange);
        return () => mql.removeEventListener?.("change", onChange);
    }, []);

    const handlePageChange = (e: PaginatorPageChangeEvent) => {
        setFirst(e.first);
        setPageCount(e.rows);
        onPageChange?.(e.page + 1, e.rows);
    };

    return (
        <Wrapper>
            <Container>
                <HeaderRow>
                    <HeaderTr>
                        {columns.map((col, i) => (
                            <HeaderCell key={i} width={col.width} flex={col.flex}>{col.title}</HeaderCell>
                        ))}
                    </HeaderTr>
                </HeaderRow>
                <tbody>
                {(displayData.length === 0 || loading) && (
                    <NoDataRow>
                        <NoDataCell colSpan={columns.length}>
                            {loading ? "Loading..." : "No Data Found"}
                        </NoDataCell>
                    </NoDataRow>
                )}
                {!loading && displayData.map((row, i) => (
                    <Row key={i}>
                        {columns.map((col, j) => <CellItem key={j} width={col.width}
                                                       flex={col.flex}>{col.render(row)}</CellItem>)}
                    </Row>
                ))}
                </tbody>
            </Container>
            {totalRecords > 9 && (
                <PaginatorBar>
                    <Paginator first={first} rows={pageCount} totalRecords={totalRecords}
                               onPageChange={handlePageChange} pageLinkSize={isMobile ? 1 : 5}
                               template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"/>
                </PaginatorBar>
            )}
        </Wrapper>
    );
};

const Wrapper = styled.div`display: flex;
    flex-direction: column;
    width: 100%;
    gap: 10px;`;

const Container = styled.table`display: flex;
    flex-direction: column;
    width: 100%;`;

const HeaderCell = styled.th<{ width?: string; flex?: string; }>`
    color: #00000080;
    font-weight: 600;
    font-size: 14px;
    line-height: 18px;
    width: ${p => p.width};
    flex: ${p => p.flex};
    text-align: start;

    @media (max-width: 768px) {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    @media (max-width: 460px) {
        width: 150px;
    }
`;

const HeaderRow = styled.thead`
    display: flex;
    background: #F6F6F6;
    border: none;
    border-radius: 10px;
    height: 40px;
`;

const Row = styled.tr`display: flex;
    align-items: center;
    height: 47px;
    border-radius: 10px;
    padding: 14px 12px;

    &:nth-child(even) {
        background-color: #E9E9E9;
    }`;

const Cell = styled.td<{ width?: string; flex?: string; }>`display: flex;
    align-items: center;
    width: ${p => p.width};
    flex: ${p => p.flex};
    font-family: SF Pro Display;
    font-weight: 600;
    font-size: 13px;
    line-height: 18px;

    @media (max-width: 460px) {
        width: 150px;
    }
`;

const HeaderTr = styled.tr`display: flex;
    padding: 0 12px;
    align-items: center;
    width: 100%;
`;

const NoData = styled.div`width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    color: #00000080;
    font-weight: 600;
    font-size: 14px;`;

const PaginatorBar = styled.div`width: 100%;
    display: flex;
    justify-content: center;
    padding: 10px 0;
    background: #fff;
    border-top: 1px solid rgba(0, 0, 0, 0.08);`;

const CellItem = styled(Cell)`
    span {
        font-family: SF Pro Display;
        font-weight: 600 !important;
        font-size: 13px;
        line-height: 18px;
    }

    @media (max-width: 768px) {
        span {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            width: 100px;
        }
    }
`;

const NoDataRow = styled.tr``;

const NoDataCell = styled.td`
  width: 100%;
  padding: 16px;
  text-align: center;
  color: #00000080;
  font-weight: 600;
  font-size: 14px;
`;
